import { FaCheck } from 'react-icons/fa';
import { PiArchiveFill } from 'react-icons/pi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  clsxMerge,
  SectionLayout,
  Spinner,
  Typography,
} from '@breezeai-frontend/cargo-ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { capitalize } from 'lodash-es';
import invariant from 'tiny-invariant';

import { useFeatureToggle, useUser } from '../../../context/auth/auth-hooks';
import { useTracking } from '../../../utils/snowplow/hooks';
import { DeclarationCancellationModal } from '../declarations/DeclarationCancellationModal';
import { DeclarationCreationModal } from '../declarations/DeclarationCreationModal/DeclarationCreationModal';
import { DeclarationCreationOutcomeModal } from '../declarations/DeclarationCreationOutcomeModal/DeclarationCreationOutcomeModal';
import { DeclarationsTable } from '../declarations/DeclarationsTable/DeclarationsTable';
import { StorageCreationModal } from '../locations/StorageCreationModal/StorageCreationModal';
import {
  StorageLocationFileTypeEnum,
  StorageLocationStatusEnum,
} from '../locations/types';
import { storageLocationQueries } from '../network/queries';
import { type StorageRouteParams } from '../types';
import { Documents } from './Documents';
import { Metrics } from './Metrics';
import { Notes } from './Notes';
import { StorageDetailsActions } from './StorageDetailsActions';
import { StorageLocationDetailsSectionLayout } from './StorageLocationDetailsSectionLayout';

// TODO use existing StatusBadge component from '../../../components/StatusBadge/StatusBadge';
const StatusBadge = ({ status }: { status: StorageLocationStatusEnum }) => (
  <div
    className={clsxMerge(
      status === StorageLocationStatusEnum.ACTIVE
        ? 'bg-system-green-100'
        : 'bg-system-grey-300',
      'flex gap-1 items-center px-2 py-1 rounded-full ml-2.5',
    )}
  >
    {status === StorageLocationStatusEnum.ACTIVE && (
      <FaCheck size={14} className="text-system-green-500" />
    )}
    {status === StorageLocationStatusEnum.ARCHIVED && (
      <PiArchiveFill size={14} className="text-system-grey-700" />
    )}
    <Typography level="subtext" color="primary" text="capitalize">
      {status}
    </Typography>
  </div>
);

// TODO Create skeleton loader for this component that inherits width and height from the parent component
export const StorageDetailsPage = ({
  declarationCreationModalOpen = false,
  declarationCancellationModalOpen = false,
}: {
  declarationCreationModalOpen?: boolean;
  declarationCancellationModalOpen?: boolean;
}) => {
  const enableStorageV2 = useFeatureToggle('enable_storage_v2');
  useTracking();

  const { locationId } = useParams<StorageRouteParams>();
  invariant(locationId, 'Location ID is required');

  const user = useUser();
  const isAuthorized = Boolean(user?.permissions.storage_locations?.view);
  const isAuthorizedToCreateDeclarations =
    user?.permissions.storage_locations?.create_declaration;
  const isAuthorizedToCancelDeclarations =
    user?.permissions.storage_locations?.cancel_declaration;
  const navigate = useNavigate();
  // TODO make strictly typed using zod
  const { state } = useLocation();
  const { declarationCreationResponse } = state ?? {};

  const { data } = useQuery(
    storageLocationQueries.details({
      locationId,
    }),
  );

  const { data: distributors } = useSuspenseQuery(
    storageLocationQueries.distributors({
      locationId,
    }),
  );

  return (
    <SectionLayout
      isAuthorized={isAuthorized}
      data-testid="storage-details-page"
      title={data?.location_name ? capitalize(data.location_name) : ''}
      backLink={enableStorageV2 ? '/storage/locations' : '/storage'}
      actions={data && <StorageDetailsActions {...data} />}
      statusBadge={data && <StatusBadge status={data.status} />}
    >
      {data ? (
        <div className="flex flex-col gap-6 w-full mt-4 sm:mt-0 min-h-fit">
          <Metrics data={data} />
          <StorageLocationDetailsSectionLayout
            title="Declarations"
            divider={false}
          >
            <div className="h-[673px] w-full">
              <DeclarationsTable />
            </div>
          </StorageLocationDetailsSectionLayout>
          <Documents
            type={StorageLocationFileTypeEnum.SURVEY_REPORT}
            data={data}
          />
          <Documents type={StorageLocationFileTypeEnum.OTHER} data={data} />
          <Notes notes={data?.notes} />
        </div>
      ) : (
        <Spinner />
      )}
      <StorageCreationModal />
      {/* Open state dependant on path */}
      {isAuthorizedToCreateDeclarations && (
        <DeclarationCreationModal
          distributors={distributors}
          isOpen={Boolean(declarationCreationModalOpen)}
          isDismissable
          showExitButton
          onExit={() => {
            navigate(
              enableStorageV2
                ? `/storage/locations/${locationId}`
                : `/storage/details/${locationId}`,
            );
          }}
          onCreation={(declarationCreationResponse) => {
            navigate(
              enableStorageV2
                ? `/storage/locations/${locationId}`
                : `/storage/details/${locationId}`,
              {
                state: { declarationCreationResponse },
              },
            );
          }}
          navigateToOnCancel={
            enableStorageV2
              ? `/storage/locations/${locationId}`
              : `/storage/details/${locationId}`
          }
        />
      )}

      {enableStorageV2 && isAuthorizedToCancelDeclarations && (
        <DeclarationCancellationModal
          isOpen={Boolean(declarationCancellationModalOpen)}
          navigateToOnSuccess={`/storage/locations/${locationId}`}
          onOpenChange={(isOpen: boolean) =>
            !isOpen && navigate(`/storage/locations/${locationId}`)
          }
        />
      )}

      <DeclarationCreationOutcomeModal
        isOpen={!!declarationCreationResponse}
        onExit={() =>
          navigate(
            enableStorageV2
              ? `/storage/locations/${locationId}`
              : `/storage/details/${locationId}`,
            {
              state: {},
            },
          )
        }
        {...declarationCreationResponse}
      />
    </SectionLayout>
  );
};
